import React, { useEffect, useState } from 'react'
import {
  ReactivationPageStyled,
  ReactivationPageSubtitle,
  ReactivationPageTitle,
} from 'Features/ReactivationPage/ReactivationPage.styled'
import { AuthPageWrapper } from 'Features/auth/common/AuthTemplate/AuthPageWrapper'
import { Trans, useTranslation } from 'react-i18next'
import { AccountsAndPlans } from 'Features/accountSettings/routes/AccountSettings/AccountsAndPlans/AccountsAndPlans'
import { LargeAuthButton } from 'Components/Elements/LargeAuthButton/LargeAuthButton.styled'
import { LoginLink } from 'Features/auth/components/SignUpScreen/SignUpScreen.styled'
import { useLogout } from 'Utils/logout'
import {
  ReactivationPageError,
  ReactivationPageLogout,
} from 'Features/accountSettings/routes/AccountSettings/AccountsAndPlans/AccountsAndPlans.styles'
import { useGetReactivationAccounts } from 'Features/ReactivationPage/api/getReactivationAccounts'
import { useNavigate } from 'react-router-dom'
import { useLoginUser } from 'Features/auth/hooks/login'
import { CheqLoader } from '@clickcease/cc-client-components-lib'
import { useUserStore } from 'Stores/userStoreModuleFed'
import { useGetPlans } from 'Features/pricing/api/getPlans'
import { useGetPlan } from 'Features/pricing/hooks/useGetPlan'
import { ProductTypes } from 'Types/ProductTypes'
import { PlanTypes } from 'Types/PlansTypes'
import { Plan } from 'Features/pricing/types/Plan'

export const ReactivationPage = () => {
  const { t } = useTranslation('accountSettings')
  const [selectedAccount, setSelectedAccount] = useState<number>()
  const plans = useGetPlans()
  const { findPlan } = useGetPlan()
  const { logOutUser } = useLogout()
  const { data: accounts, isFetched: isAccountFetched } = useGetReactivationAccounts()
  const navigate = useNavigate()
  const { setReactivationPrevPlans } = useUserStore()
  const { switchAccount } = useLoginUser()
  const [error, setError] = useState<string>('')
  const accountSelected = () => {
    //validate account selected
    if (!selectedAccount) {
      setError(t('reactivation.please_select_account'))
      return
    } else {
      setError('')
    }

    //find selected account
    const accountToSwitch = accounts.find((account) => account.id === selectedAccount)
    if (accountToSwitch) {
      const account = {
        accountId: accountToSwitch.id,
        accountName: accountToSwitch.name,
        owner: true,
        networkId: accountToSwitch.networkId.toString(),
        activeSubscription: false,
        subscriptionId: accountToSwitch.subscriptions[0].id,
        migrated: false,
      }

      //set selected account
      switchAccount(account)
      const productA =
        accountToSwitch?.subscriptions[accountToSwitch?.subscriptions.length - 1].Plan[0].product

      //look for plan A (yearly)
      const planA = findPlan(
        plans.data || [],
        productA?.planType as PlanTypes,
        productA?.currency,
        productA?.product as ProductTypes,
        productA?.visitsAllowed / 1000,
        12
      )
      if (accountToSwitch?.subscriptions[0].Plan.length === 1) {
        setReactivationPrevPlans([planA?.id || 0])

        navigate('/checkout', {
          state: {
            reactivation: true,
            billingCycleMonths: productA?.billingCycleMonths,
            planName: t(
              `${productA.product.toLowerCase()}_plan_${productA.planType.toLowerCase()}`
            ),
            planType: planA?.planType,
            planId: planA?.id,
            allowedVisits: planA?.visitsAllowed,
            currency: planA?.currency,
            amount: planA?.amount,
            pricingId: planA?.pricingId,
          },
        })
      } else {
        const productB = accountToSwitch.subscriptions[0].Plan[1].product
        setReactivationPrevPlans([
          planA?.id || 0,
          accountToSwitch.subscriptions[0].Plan[1].product.id,
        ])
        const planB = findPlan(
          plans.data || [],
          productB?.planType as PlanTypes,
          productB?.currency,
          productB?.product as ProductTypes,
          productB?.visitsAllowed / 1000,
          12
        )

        let paidMarketingPlan: Plan | undefined
        let botMitigationPlan: Plan | undefined
        if (planA?.productType.toLowerCase() === 'paidmarketing') {
          paidMarketingPlan = planA
          botMitigationPlan = planB
        } else {
          paidMarketingPlan = planB
          botMitigationPlan = planA
        }

        navigate('/checkout/?bundle', {
          state: {
            reactivation: true,
            paidMarketingPlan,
            botMitigationPlan,
            currency: planA?.currency,
            billingCycleMonths: productA.billingCycleMonths,
          },
        })
      }
    }
  }

  useEffect(() => {
    if (accounts && accounts.length === 1) {
      setSelectedAccount(accounts[0].id)
    }
  }, [accounts])

  return (
    <AuthPageWrapper>
      <ReactivationPageStyled>
        {!isAccountFetched ? (
          <CheqLoader />
        ) : (
          <>
            <ReactivationPageTitle>{t('reactivation.title')}</ReactivationPageTitle>
            <ReactivationPageSubtitle>{t('reactivation.subtitle')}</ReactivationPageSubtitle>
            <AccountsAndPlans
              selectedAccount={selectedAccount}
              setSelectedAccount={setSelectedAccount}
            />
            <LargeAuthButton onClick={accountSelected}>
              {t('reactivation.reactivate')}
            </LargeAuthButton>
            <ReactivationPageError>{error}</ReactivationPageError>
            <ReactivationPageLogout>
              <Trans
                ns="auth"
                i18nKey="switch-account.logout"
                components={{ 1: <LoginLink onClick={logOutUser} /> }}
              />
            </ReactivationPageLogout>
          </>
        )}
      </ReactivationPageStyled>
    </AuthPageWrapper>
  )
}
